<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { Category } from "../composables/useItemList.js";
import { MAX_QUERY_LENGTH } from "../search.js";
import { store } from "../store.js";

/**
 * Compact filter strip: search + one chips row. The date-range inputs used to
 * sit permanently expanded here, costing ~56px of chrome nobody used on most
 * visits — they now live behind the "วันที่" chip (Drive's filter-chip
 * pattern) and expand only on demand.
 *
 * The category tabs (รูปภาพ / ไฟล์ / ลิงก์) are exclusive — there is no "all"
 * tab — and the grid-vs-list view is derived from the category (media → grid),
 * so there is no manual view toggle either.
 */
const props = defineProps<{
  category: Category;
  dateFrom: string;
  dateTo: string;
  q: string;
  /**
   * A server search is still owed for what's typed. Results are already on
   * screen by then (the local pass paints on the keystroke), so this is not a
   * "wait" signal — it's the quiet admission that the archive is larger than
   * what could be checked instantly. Shown in the field itself rather than as a
   * blocking spinner over the list.
   */
  pending?: boolean;
}>();

const searchInput = ref<HTMLInputElement | null>(null);
// The header search icon (App.vue) bumps this token; it has no other way to
// reach into whichever page's FilterBar is currently mounted.
watch(
  () => store.searchFocusToken,
  () => searchInput.value?.focus()
);

const emit = defineEmits<{
  "update:category": [value: Category];
  "update:dateFrom": [value: string];
  "update:dateTo": [value: string];
  "update:q": [value: string];
}>();

const categoryOptions: { value: Category; label: string }[] = [
  { value: "MEDIA", label: "รูปภาพ" }, // images + videos
  { value: "FILE", label: "ไฟล์" },
  { value: "LINK", label: "ลิงก์" },
];

const showDates = ref(false);
const dateActive = computed(() => props.dateFrom !== "" || props.dateTo !== "");
const dateChipLabel = computed(() => {
  if (!dateActive.value) return "วันที่";
  // Parsed as a local calendar day, not via `new Date("YYYY-MM-DD")` — that
  // reads as UTC midnight and would print the previous day west of Greenwich,
  // i.e. the chip would disagree with the date the user just picked.
  const fmt = (v: string) => {
    const parts = v.split("-").map(Number);
    return parts.length === 3 && parts.every(Number.isFinite)
      ? new Date(parts[0]!, parts[1]! - 1, parts[2]!).toLocaleDateString("th-TH", { day: "numeric", month: "short" })
      : "…";
  };
  return `${fmt(props.dateFrom)} – ${fmt(props.dateTo)}`;
});

/**
 * The two pickers bound to each other, plus today as a ceiling on both.
 *
 * A reversed range is trivially easy to tap out and can only ever return zero
 * rows — an empty list with no explanation, since nothing in the UI says the
 * range is impossible. Constraining the inputs makes it unpickable instead of
 * making it an error to recover from. The future is capped for the same reason:
 * the archive cannot contain anything sent tomorrow.
 */
const today = computed(() => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
});

function clearDates(): void {
  emit("update:dateFrom", "");
  emit("update:dateTo", "");
  showDates.value = false;
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center gap-2">
      <div class="relative min-w-0 flex-1">
        <span
          class="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[20px] text-on-surface-variant"
          >search</span
        >
        <input
          ref="searchInput"
          class="h-11 w-full rounded-lg border border-outline-variant bg-surface-container-lowest pl-10 pr-12 font-body-sm text-on-surface outline-none transition-colors placeholder:text-on-surface-variant focus:border-primary focus:ring-1 focus:ring-primary"
          type="search"
          enterkeyhint="search"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          :maxlength="MAX_QUERY_LENGTH"
          aria-label="ค้นหาในคลัง"
          placeholder="ค้นชื่อไฟล์ / ชื่อลิงก์..."
          :value="q"
          @input="emit('update:q', ($event.target as HTMLInputElement).value)"
        />
        <!-- One trailing slot, two mutually exclusive occupants: the in-flight
             ring while the server pass is owed, otherwise a clear button. They
             share the slot so the field never changes width or reflows the row.
             The ring is decorative here — the list carries the spoken status. -->
        <span
          v-if="pending"
          class="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[16px] text-on-surface-variant"
          aria-hidden="true"
        >
          <span class="spin" />
        </span>
        <button
          v-else-if="q"
          type="button"
          aria-label="ล้างคำค้นหา"
          class="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg text-on-surface-variant transition-colors active:bg-surface-container"
          @click="emit('update:q', '')"
        >
          <span class="material-symbols-outlined text-[18px]">close</span>
        </button>
      </div>
    </div>

    <div class="no-scrollbar flex gap-2 overflow-x-auto">
      <button
        v-for="opt in categoryOptions"
        :key="opt.value"
        type="button"
        class="h-9 shrink-0 whitespace-nowrap rounded-full px-5 font-label-caps transition-all active:scale-95"
        :class="
          props.category === opt.value
            ? 'bg-primary text-on-primary'
            : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-dim'
        "
        @click="emit('update:category', opt.value)"
      >
        {{ opt.label }}
      </button>

      <button
        type="button"
        class="flex h-9 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-4 font-label-caps transition-all active:scale-95"
        :class="
          dateActive || showDates
            ? 'bg-primary text-on-primary'
            : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-dim'
        "
        :aria-expanded="showDates"
        @click="showDates = !showDates"
      >
        <span class="material-symbols-outlined text-[16px]">calendar_month</span>
        {{ dateChipLabel }}
      </button>
    </div>

    <div v-if="showDates" class="flex items-center gap-2">
      <input
        type="date"
        aria-label="ตั้งแต่วันที่"
        class="h-10 min-w-0 flex-1 rounded-lg border border-outline-variant bg-surface-container-lowest px-3 font-body-sm text-on-surface outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary"
        :max="dateTo || today"
        :value="dateFrom"
        @input="emit('update:dateFrom', ($event.target as HTMLInputElement).value)"
      />
      <span class="font-body-sm text-on-surface-variant">–</span>
      <input
        type="date"
        aria-label="ถึงวันที่"
        class="h-10 min-w-0 flex-1 rounded-lg border border-outline-variant bg-surface-container-lowest px-3 font-body-sm text-on-surface outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary"
        :min="dateFrom || undefined"
        :max="today"
        :value="dateTo"
        @input="emit('update:dateTo', ($event.target as HTMLInputElement).value)"
      />
      <button
        v-if="dateActive"
        type="button"
        aria-label="ล้างตัวกรองวันที่"
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-on-surface-variant transition-colors active:bg-surface-container-high"
        @click="clearDates"
      >
        <span class="material-symbols-outlined text-[18px]">close</span>
      </button>
    </div>
  </div>
</template>
